import _defineProperty from "/Users/matt/dev/github.com/industriousapps/excelkits-client/node_modules/.pnpm/next@13.5.7_@babel+core@7.25.2_react-dom@18.3.1_react@18.3.1__react@18.3.1_sass@1.79.3/node_modules/next/dist/compiled/@babel/runtime/helpers/esm/defineProperty.js";

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import clsx from 'clsx';
import React, { useEffect, useMemo, useState } from 'react';
import { Radar } from 'react-chartjs-2';
import { chartColorPallet as DEFAULT_COLORS } from '@/config/colorPallets/universalDesignSystem';
import templateEngine from '@/utils/templateEngine';
import settings, { selectionConfig, labelsSelectionConfig, legendsSelectionConfig } from './settings';
import { deleteUndefinedValues } from '@/utils/objects';
import { extractDataLabelProperty, getSectionCanvasId } from '../internal/utils';
import { hasAnySelectionData } from '../../utils/selections';
import useSelections from '../../misc/useSelections';
import useSelectionLabels from '../../misc/useSelectionLabels';
import useSelectionLegends from '../../misc/useSelectionLegends';
import useSubscribeWidgetResults from '../hooks/useSubscribeWidgetResults';
import useLineEditingArea from '../internal/useLineEditingArea';
import useChartActions from '../internal/useChartActions';
import { useRadarStyles } from './stylesTheme';
import useData from './useData';
import StyleVariables, { getSectionAttr, getSectionAttrSelector } from '../../misc/StyleVariables';
import Header from '../misc/Header';
import Loading from './Loading';
import styles from './styles.module.scss';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";

const RadarChart = _ref => {
  let {
    widgetId,
    sectionId,
    elementId,
    editing = false,
    reportingWidgetId,
    config
  } = _ref;
  const {
    themeStyles,
    themeCtx
  } = useRadarStyles(config);
  const {
    colorPallet: defaultColorPallet = DEFAULT_COLORS
  } = themeCtx;
  const {
    animation = {},
    selections = '',
    labelsSelections = '',
    legendsSelections = '',
    selectionsLabeled = true,
    selectionsStrategy = 'default',
    globalSeriesOptions,
    seriesOptions = [],
    collectionOptions = [],
    colorPallet = defaultColorPallet
  } = config;
  const results = useSubscribeWidgetResults(reportingWidgetId); // Get relevant results for each selection

  const {
    selectionResults,
    resultsUpdated
  } = useSelections({
    results,
    selections,
    selectionsLabeled,
    selectionConfig
  });
  const {
    selectionLabels
  } = useSelectionLabels({
    results,
    resultsUpdated,
    selectionsLabeled,
    labelsSelectionConfig,
    labelsSelections,
    labelsTemplate: globalSeriesOptions === null || globalSeriesOptions === void 0 ? void 0 : globalSeriesOptions.labelsTemplate
  });
  const {
    selectionLegends
  } = useSelectionLegends({
    results,
    resultsUpdated,
    legendsSelectionConfig,
    legendsSelections
  });
  const {
    chartData
  } = useData({
    results: selectionResults,
    selectionLabels,
    selectionLegends,
    seriesOptions,
    globalSeriesOptions,
    collectionOptions,
    colorPallet,
    selectionsStrategy,
    legends: config.legends,
    themeStyles
  });
  const {
    0: isLoadingDeps,
    1: setIsLoadingDeps
  } = useState(false);
  const {
    0: hasLoadedDeps,
    1: setHasLoadedDeps
  } = useState(false);
  const {
    handleChartClick
  } = useLineEditingArea({
    widgetId,
    sectionId,
    chartData
  });
  const {
    openLegendsContextEditor
  } = useChartActions({}); // Load Chart Deps

  useEffect(() => {
    if (isLoadingDeps || hasLoadedDeps) {
      return;
    }

    setIsLoadingDeps(true);

    (async () => {
      const {
        default: Chart,
        RadialLinearScale,
        PointElement,
        LineElement,
        Filler,
        Tooltip,
        Legend
      } = await import('chart.js/auto');
      const {
        default: ChartDataLabels
      } = await import('chartjs-plugin-datalabels');
      const {
        default: CustomBackground
      } = await import('../plugins/customBackground');
      const {
        default: ActiveLineHighlight
      } = await import('../plugins/activeLineHighlightPlugin');
      Chart.register(RadialLinearScale, PointElement, LineElement, Filler, Tooltip, Legend, ChartDataLabels, CustomBackground, ActiveLineHighlight); // Disable datalabels by default

      if (Chart.defaults.plugins.datalabels) {
        Chart.defaults.plugins.datalabels.display = false;
      }

      setHasLoadedDeps(true);
      setIsLoadingDeps(false);
    })(); // eslint-disable-next-line react-hooks/exhaustive-deps

  }, []); // Data & Loading States

  const hasDrawableData = Boolean(chartData.datasets.length);
  const isShowingLoader = !hasLoadedDeps || !hasDrawableData;
  const hasNoData = !hasAnySelectionData(selectionResults);
  const isLoading = !results.length;
  const chartRenderingHash = Object.entries(animation).map(_ref2 => {
    let [key, value] = _ref2;
    return `${key}:${value}`;
  }).join('.'); // Create chart options

  const options = useMemo(() => {
    var _config$styles, _config$globalSeriesO, _chartData$dataBounda, _chartData$dataBounda2, _config$styles2;

    const animation = config.animation || {};
    const chartBackgroundColor = (config === null || config === void 0 ? void 0 : (_config$styles = config.styles) === null || _config$styles === void 0 ? void 0 : _config$styles.backgroundColor) || themeStyles.chartBackgroundColor;
    const chartTitle = config.chartTitle ? {
      display: true,
      text: config.chartTitle,
      font: {
        family: themeStyles.titleFontFamily,
        size: themeStyles.titleFontSize,
        lineHeight: themeStyles.titleLineHeight
      },
      color: themeStyles.titleColor
    } : {};
    const legendPosition = config.legendPosition || 'bottom';
    const hasLegend = Boolean(config.hasLegend);
    const isAnimationDisabled = animation.enabled !== undefined && !animation.enabled;
    const axisFont = {
      font: {
        family: themeStyles.chartAxisFontFamily,
        size: themeStyles.chartAxisFontSize,
        weight: themeStyles.chartAxisFontWeight
      },
      color: themeStyles.chartAxisFontColor,
      backdropColor: chartBackgroundColor || themeStyles.bodyBackgroundColor
    };
    return {
      responsive: true,
      maintainAspectRatio: false,
      pointRadius: themeStyles.pointRadius,
      pointStyle: ((_config$globalSeriesO = config.globalSeriesOptions) === null || _config$globalSeriesO === void 0 ? void 0 : _config$globalSeriesO.pointStyle) ?? 'circle',
      pointBorderWidth: 0,
      animation: _objectSpread(_objectSpread({}, deleteUndefinedValues(animation)), {}, {
        duration: isAnimationDisabled ? 0 : animation.duration
      }),
      scales: {
        r: {
          grid: {
            color: themeStyles.chartAxisGridColor
          },
          angleLines: {
            color: themeStyles.chartAngleLineColor ?? themeStyles.chartAxisGridColor
          },
          ticks: _objectSpread(_objectSpread({}, axisFont), {}, {
            display: themeStyles.chartAxisTicksVisible ?? true,
            callback: function (value) {
              if (chartData.valueAxisTemplate) {
                return templateEngine(chartData.valueAxisTemplate, {
                  value: value
                });
              }

              if (typeof value === 'string') return value;
              return this.getLabelForValue(value);
            }
          }),
          pointLabels: axisFont,
          suggestedMax: (_chartData$dataBounda = chartData.dataBoundaries) === null || _chartData$dataBounda === void 0 ? void 0 : _chartData$dataBounda.max,
          suggestedMin: (_chartData$dataBounda2 = chartData.dataBoundaries) === null || _chartData$dataBounda2 === void 0 ? void 0 : _chartData$dataBounda2.min
        }
      },
      plugins: {
        title: chartTitle,
        datalabels: {
          display: true,
          borderRadius: themeStyles.chartDataLabelBorderRadius,
          color: context => extractDataLabelProperty(context, 'color', themeStyles.chartDataLabelColor),
          align: context => extractDataLabelProperty(context, 'align', 'center'),
          anchor: context => extractDataLabelProperty(context, 'anchor', 'end'),
          rotation: context => extractDataLabelProperty(context, 'rotation', 0),
          clamp: context => extractDataLabelProperty(context, 'clamp', true),
          formatter: function (value, context) {
            var _context$dataset$data;

            // Hide labels if not enabled or with value 0
            const dataLabel = (_context$dataset$data = context.dataset.datalabels) === null || _context$dataset$data === void 0 ? void 0 : _context$dataset$data[context.dataIndex];
            if (!(dataLabel !== null && dataLabel !== void 0 && dataLabel.display) || value === 0) return '';
            return dataLabel.template ?? value;
          },
          backgroundColor: function (context) {
            var _context$dataset$data2;

            // Hide labels if not enabled or with value 0
            const dataLabel = (_context$dataset$data2 = context.dataset.datalabels) === null || _context$dataset$data2 === void 0 ? void 0 : _context$dataset$data2[context.dataIndex];
            if (!(dataLabel !== null && dataLabel !== void 0 && dataLabel.display)) return '';
            return themeStyles.chartDataLabelBackgroundColor;
          },
          font: axisFont.font
        },
        legend: deleteUndefinedValues(_objectSpread({
          align: ['top', 'bottom'].includes(legendPosition) ? 'start' : undefined,
          position: legendPosition,
          display: hasLegend,
          labels: {
            pointStyle: 'rectRounded',
            usePointStyle: true,
            font: {
              family: themeStyles.legendFontFamily,
              size: themeStyles.legendFontSize,
              lineHeight: themeStyles.legendLineHeight
            },
            color: themeStyles.legendColor,
            // Remove unwanted datasets from legend
            filter: item => item.text !== undefined
          }
        }, editing && openLegendsContextEditor && {
          onClick: e => openLegendsContextEditor(e, widgetId, sectionId)
        })),
        tooltip: {
          enabled: true,
          usePointStyle: true,
          intersect: false,
          cornerRadius: 0,
          callbacks: {
            title: _ref3 => {
              let [item] = _ref3;
              // @ts-ignore
              return (item === null || item === void 0 ? void 0 : item.dataset.titles[item.dataIndex]) || item.label;
            },
            label: item => {
              return ` ${// @ts-ignore
              (item === null || item === void 0 ? void 0 : item.dataset.templates[item.dataIndex]) || item.parsed.y}`;
            }
          }
        },
        activeLineHighlightPlugin: {
          enable: editing,
          dimOpacity: 0.15,
          // Opacity for non-hovered lines.
          hoverCursor: 'pointer',
          hoverDistanceThreshold: 10 // Pixels: how close the mouse must be to a line.

        },
        customCanvasBackgroundColor: {
          color: (config === null || config === void 0 ? void 0 : (_config$styles2 = config.styles) === null || _config$styles2 === void 0 ? void 0 : _config$styles2.backgroundColor) || themeStyles.chartBackgroundColor
        }
      },
      layout: {
        padding: {
          left: 0,
          right: 0,
          top: 0,
          bottom: 0
        }
      },
      onClick: editing ? handleChartClick : undefined
    };
  }, [config, themeStyles, chartData.dataBoundaries, chartData.valueAxisTemplate, editing, handleChartClick, openLegendsContextEditor, widgetId, sectionId]);
  return /*#__PURE__*/_jsxs("div", _objectSpread(_objectSpread(_objectSpread({}, elementId && {
    id: elementId
  }), {}, {
    className: styles.container
  }, getSectionAttr(widgetId, sectionId)), {}, {
    children: [/*#__PURE__*/_jsx(Header, {
      editing: editing,
      widgetId: widgetId,
      sectionId: sectionId,
      config: config,
      className: styles.header
    }), isLoading || hasNoData ? /*#__PURE__*/_jsx(Loading, {
      editing: editing
    }) : /*#__PURE__*/_jsxs(_Fragment, {
      children: [isShowingLoader ? /*#__PURE__*/_jsx(Loading, {
        editing: editing
      }) : /*#__PURE__*/_jsx(_Fragment, {}), /*#__PURE__*/_jsx("div", {
        className: clsx(styles.main, isShowingLoader ? styles['main--hide'] : ''),
        children: hasLoadedDeps ? /*#__PURE__*/_jsx(Radar, {
          id: getSectionCanvasId(widgetId, sectionId) // @ts-expect-error dataset types mismatch
          ,
          data: chartData,
          options: options
        }, chartRenderingHash) : /*#__PURE__*/_jsx(_Fragment, {})
      })]
    }), /*#__PURE__*/_jsx(StyleVariables, {
      selector: getSectionAttrSelector(widgetId, sectionId),
      data: _objectSpread(_objectSpread({}, (settings === null || settings === void 0 ? void 0 : settings.styles) || {}), (config === null || config === void 0 ? void 0 : config.styles) || {})
    })]
  }));
};

export default RadarChart;
export { DEFAULT_COLORS, useSelections, selectionConfig, labelsSelectionConfig, useData };